import { App } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import { useEffect } from "react";
import { apolloClient } from "./apollo-client";

const REFETCH_QUERIES = [
  "Appointments",
  "WaitingToConfirmAppointments",
  "DashboardMetrics",
  "CalendarAppointments",
];

/**
 * Triggers a refetch of appointment-related queries whenever the app comes to
 * the foreground or the network reconnects.
 *
 * On native iOS, `App.addListener("appStateChange")` fires from the real OS
 * resume callback. On web it falls back to `visibilitychange` automatically,
 * so this hook is cross-platform without additional branching.
 *
 * This is the guaranteed "wake-up" trigger that covers the case where the
 * GraphQL WebSocket subscription died silently while the app was backgrounded
 * and the global `skipPollAttempt` pause prevented polling from catching up.
 */
export function useAppLifecycleRefetch() {
  useEffect(() => {
    let appStateHandle: PluginListenerHandle | null = null;
    let removed = false;

    const refetchAll = () => {
      apolloClient.refetchQueries({ include: REFETCH_QUERIES });
    };

    // Register Capacitor appStateChange listener (native iOS + web fallback)
    void App.addListener("appStateChange", (state) => {
      if (state.isActive) {
        refetchAll();
      }
    }).then((handle) => {
      // Guard against the component unmounting before the promise resolves
      if (removed) {
        handle.remove();
      } else {
        appStateHandle = handle;
      }
    });

    // Register window online listener for network reconnect
    window.addEventListener("online", refetchAll);

    return () => {
      removed = true;
      window.removeEventListener("online", refetchAll);
      appStateHandle?.remove();
    };
  }, []);
}
